/* eslint-disable id-length */

import "~/components/Playground/style.css";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type Edge,
  type Node,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  applyNodeChanges,
  applyEdgeChanges,
} from "@xyflow/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import Database from "~/lib/Database";
import { EdgeData, NodeType, State, Step } from "~/lib/pipeline";
import { StepDependencyContext } from "~/components/build/Show/lib/types";
import { createGraph } from "~/components/Playground/useGraph";
import usePositionedGraph from "~/components/Playground/usePositionedGraph";
import { nodeTypes } from "~/components/Playground/Node";
import EdgeCanvas from "~/components/Playground/EdgeCanvas";
import Background from "~/components/Playground/Background";
import { ControlButton, ControlPanel } from "~/components/Playground/Controls/ControlButton";
import { GroupStepsControl } from "~/components/Playground/Controls/GroupStepsControl";
import { SkippedStepsControl } from "~/components/Playground/Controls/SkippedStepsControl";
import { ZoomControls } from "~/components/Playground/Controls/ZoomControls";
import Icon from "~/components/shared/Icon";
import Spinner from "~/components/shared/Spinner";
import { useExpandedGroups } from "~/components/Playground/useExpandedGroups";
import { useFitViewIfPossible } from "~/components/Playground/useFitViewIfPossible";
import { Emphasis } from "~/components/Playground/Node/BaseNode";

interface AdminBuildGraphProps {
  steps: Step[];
  dependenciesByStep: Record<Step["uuid"], StepDependencyContext>;
}

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 1;
const FOCUS_ZOOM = 0.8;

function setNodeEmphasis(node: Node, emphasis: Emphasis): Node {
  return {
    ...node,
    data: { ...node.data, emphasis },
  };
}

function AdminBuildGraphInner({ steps, dependenciesByStep }: AdminBuildGraphProps) {
  const [initialisedNodes, setInitialisedNodes] = useState(false);
  const [initialisedView, setInitialisedView] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [removeIgnoredSteps, setRemoveIgnoredSteps] = useState(true);
  const { getNodes } = useReactFlow();
  const fitViewIfPossible = useFitViewIfPossible(canvasRef.current);

  const { expandedGroupSteps, toggleGroupStep, expandAllGroups, collapseAllGroups } =
    useExpandedGroups({
      steps,
      defaultExpandFailed: true,
    });

  const totalIgnoredSteps = useMemo(() => {
    return steps.filter((step) => step.state === State.Ignored).length;
  }, [steps]);

  const totalGroupSteps = useMemo(() => {
    return steps.filter((step) => step.type === "group").length;
  }, [steps]);

  const graph = useMemo(() => {
    return createGraph(steps, dependenciesByStep, {
      removeIgnoredSteps,
      expandedGroupSteps,
    });
  }, [steps, dependenciesByStep, removeIgnoredSteps, expandedGroupSteps]);

  const onInitialised = useCallback(() => {
    setInitialisedNodes(true);
  }, []);

  const {
    nodes: positionedNodes,
    edges: positionedEdges,
    calculatingLayout,
  } = usePositionedGraph({
    ...graph,
    expandedGroupSteps,
    onInitialised,
    currentStep: null,
  });

  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge<EdgeData>[]>([]);

  useEffect(() => {
    setNodes(positionedNodes.map((node) => setNodeEmphasis(node, Emphasis.Default)));
  }, [positionedNodes]);

  useEffect(() => {
    setEdges(positionedEdges);
  }, [positionedEdges]);

  const onNodesChange = useCallback(
    (changes) => setNodes((nodes) => applyNodeChanges(changes, nodes)),
    [setNodes],
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((edges) => applyEdgeChanges(changes, edges)),
    [setEdges],
  );

  const handleFitView = useCallback(() => {
    fitViewIfPossible(getNodes(), { maxZoom: FOCUS_ZOOM });
  }, [fitViewIfPossible, getNodes]);

  useEffect(() => {
    if (initialisedNodes && !initialisedView) {
      setTimeout(() => {
        handleFitView();
        setInitialisedView(true);
      }, 25);
    }
  }, [initialisedNodes, initialisedView, handleFitView]);

  return (
    <>
      <ReactFlow
        ref={canvasRef}
        nodes={nodes}
        edges={[]}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodesDraggable={false}
        nodesConnectable={false}
        edgesReconnectable={false}
        elementsSelectable={false}
        edgesFocusable={false}
        nodesFocusable={false}
        panOnScroll={true}
        zoomOnScroll={true}
        panOnScrollSpeed={1.5}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        proOptions={{ hideAttribution: true }}
        onNodeDoubleClick={(_, node) => {
          if (node.type === NodeType.Group) {
            toggleGroupStep(node.id);
          }
        }}
      >
        {initialisedView && <EdgeCanvas edges={edges} />}
        <Background />

        {!initialisedView && <div className="absolute inset-0 z-10 bg-white" />}
      </ReactFlow>

      {calculatingLayout && <Spinner className="absolute right-4 top-4 z-10" />}

      <div className="absolute bottom-3 right-3 z-10 flex gap-2">
        {totalIgnoredSteps > 0 && (
          <SkippedStepsControl
            hidden={removeIgnoredSteps}
            onToggle={() => setRemoveIgnoredSteps(!removeIgnoredSteps)}
          />
        )}

        {totalGroupSteps > 0 && (
          <GroupStepsControl
            hasExpandedGroups={expandedGroupSteps.size > 0}
            onExpandAll={expandAllGroups}
            onCollapseAll={collapseAllGroups}
          />
        )}

        <ControlPanel>
          <ControlButton onClick={handleFitView} label="Focus view">
            <Icon icon="location-crosshairs" className="h-4 w-5" />
          </ControlButton>
        </ControlPanel>

        <ZoomControls />
      </div>
    </>
  );
}

const queryClient = new QueryClient();

export default function AdminBuildGraph(props: AdminBuildGraphProps) {
  // Transform snake_case keys from Ruby to camelCase for JavaScript steps only.
  // dependenciesByStep keys are UUIDs (no transformation needed) and its internal
  // structure uses snake_case which the TypeScript types expect.
  const parsedSteps = useMemo(() => Database.parse(props.steps) as Step[], [props.steps]);

  return (
    <QueryClientProvider client={queryClient}>
      <ReactFlowProvider>
        <div className="relative h-[600px] w-full overflow-hidden rounded-sm border border-gray-300">
          <AdminBuildGraphInner steps={parsedSteps} dependenciesByStep={props.dependenciesByStep} />
        </div>
      </ReactFlowProvider>
    </QueryClientProvider>
  );
}
